import { ErrorMessage } from 'views/components';
import PropTypes from 'prop-types';
import { configActions } from 'core/Config';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    ExpandButton,
    FormGroup,
    Input,
    Modal,
    SelectSearch
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    DoctorPicker,
    NotificationMeanPicker,
    NotificationTypePicker,
    SpecialtyPicker
} from 'views/pickers';
import React, { useEffect } from 'react';
import {
    clearObject,
    parseSelect,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';

const DEFAULT_FORM = {
    doctor_id: null,
    enabled: true,
    end_time: '',
    mean: 'email',
    prev_minutes: '',
    specialty_id: null,
    start_time: '',
    template_id: null,
    type: null
};

const ConfigNotificationModal = ({
    active,
    data = {},
    onToggle
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { clearError, errors, setErrors } = useErrors();
    const [form, setForm, setFormValue] = useObjectState({ ...DEFAULT_FORM });

    const {
        templates,
        templatesLoading
    } = useSelector('config');
    const parsedTemplates = parseSelect(templates.map(t => {
        const { options, ...rest } = t;
        return rest;
    }));

    const createMode = !data?.id;

    useEffect(() => {
        if (isEmpty(data)) return;
        if (!data?.id) {
            setForm({ ...DEFAULT_FORM });
            return;
        }
        const {
            doctor,
            enabled,
            end_time,
            id,
            mean,
            prev_minutes,
            specialty,
            start_time,
            template,
            type
        } = data;
        setForm({
            doctor_id: doctor?.id ?? null,
            enabled: !!enabled,
            end_time,
            ...(!!id && { id }),
            mean,
            prev_minutes,
            specialty_id: specialty?.id ?? null,
            start_time,
            template_id: template?.id ?? null,
            type
        });
    }, [data, setForm]);

    const modalProps = {
        active,
        onToggle,
        width: '100%'
    };

    const onSubmit = () => {
        const {
            end_time,
            prev_minutes,
            start_time,
            template_id,
            type
        } = form;
        const { errors, isValid } = validationSchema({
            end_time: [!start_time || (!!end_time && end_time >= start_time), t('Rango incorrecto')],
            template_id: [!!template_id, t('Requerido')],
            type: [!!type, t('Requerido')]
        });
        setErrors(errors);
        if (!isValid) return;
        const body = {
            ...form,
            end_time: !!end_time ? end_time : undefined,
            prev_minutes: !!prev_minutes ? prev_minutes : 0,
            start_time: !!start_time ? start_time : undefined
        };
        if (createMode) {
            dispatch(configActions.createNotification(clearObject(body)));
        } else {
            dispatch(configActions.updateNotification(clearObject(body)));
        }
        onToggle();
    };

    return (
        <Modal { ...modalProps }>
            <Row>
                <Col md="6" xs="12">
                    <FormGroup label={ t('Tipo') } margin="0px 0px 20px 0px">
                        <NotificationTypePicker
                            hasError={ !!errors?.type }
                            value={ form?.type }
                            onChange={ value => {
                                clearError('type');
                                setFormValue('type', value);
                            } }
                        />
                        <ErrorMessage relative error={ errors?.type } marginLeft={ 0 } marginTop={ 5 } />
                    </FormGroup>
                </Col>
                <Col md="6" xs="12">
                    <FormGroup label={ t('Medio') } margin="0px 0px 20px 0px">
                        <NotificationMeanPicker
                            value={ form?.mean }
                            onChange={ value => setFormValue('mean', value) }
                        />
                    </FormGroup>
                </Col>
                <Col md="4" xs="12">
                    <FormGroup label={ t('Inicio') } margin="0px 0px 20px 0px">
                        <Input
                            error={ !!errors?.end_time }
                            type="time"
                            value={ form?.start_time }
                            onChange={ value => setFormValue('start_time', value) }
                        />
                    </FormGroup>
                </Col>
                <Col md="4" xs="12">
                    <FormGroup label={ t('Fin') } margin="0px 0px 20px 0px">
                        <Input
                            error={ errors?.end_time }
                            type="time"
                            value={ form?.end_time }
                            onChange={ value => {
                                clearError('end_time');
                                setFormValue('end_time', value);
                            } }
                        />
                    </FormGroup>
                </Col>
                <Col md="4" xs="12">
                    <FormGroup label={ t('Minutos previos') } margin="0px 0px 20px 0px">
                        <Input
                            type="number"
                            value={ form?.prev_minutes }
                            onChange={ value => setFormValue('prev_minutes', value) }
                        />
                    </FormGroup>
                </Col>
                <Col md="6" xs="12">
                    <FormGroup label={ t('Profesional') } margin="0px 0px 20px 0px">
                        <DoctorPicker
                            allowNone={ true }
                            nullPlaceholder="Sin definir"
                            value={ form?.doctor_id }
                            onChange={ value => setFormValue('doctor_id', value) }
                        />
                    </FormGroup>
                </Col>
                <Col md="6" xs="12">
                    <FormGroup label={ t('Especialidad') } margin="0px 0px 20px 0px">
                        <SpecialtyPicker
                            allowNone={ true }
                            value={ form?.specialty_id }
                            onChange={ value => setFormValue('specialty_id', value) }
                        />
                    </FormGroup>
                </Col>
                <Col md="6" xs="12">
                    <FormGroup label={ t('Template') } margin="0px 0px 20px 0px">
                        <SelectSearch
                            allowAll={ true }
                            hasError={ !!errors?.template_id }
                            loading={ templatesLoading }
                            options={ parsedTemplates }
                            value={ form?.template_id }
                            width="100%"
                            onChange={ value => {
                                clearError('template_id');
                                setFormValue('template_id', value);
                            } }
                        />
                        <ErrorMessage relative error={ errors?.template_id } marginLeft={ 0 } marginTop={ 5 } />
                    </FormGroup>
                </Col>
                <Col md="6" xs="12">
                    <FormGroup label={ t('Habilitado') } margin="0px 0px 20px 0px">
                        <Checkbox
                            checked={ form.enabled }
                            onClick={ value => setFormValue('enabled', value) }
                        />
                    </FormGroup>
                </Col>
            </Row>
            <ButtonFooter margin="20px 0px">
                <ExpandButton
                    caption={ t('Volver') }
                    size={ 100 }
                    onClick={ onToggle }
                />
                <ExpandButton
                    caption={ createMode ? t('Crear') : t('Confirmar') }
                    size={ 100 }
                    onClick={ onSubmit }
                />
            </ButtonFooter>
        </Modal>
    );
};

ConfigNotificationModal.propTypes = {
    active: PropTypes.bool,
    data: PropTypes.object,
    onToggle: PropTypes.func
};

export default ConfigNotificationModal;
